/* PDF export.

   Two A4 pages, card centred on each, crop and fold marks outside the trim.
   Page 1 is the outside; page 2 is the inside with its panels already reversed
   by the renderer, which is what makes the two sides register through the
   sheet after a duplex flip.

   All text is converted to outlines before hand-off to svg2pdf: the PDF then
   needs no embedded fonts and cannot substitute a glyph, at the cost of the
   text no longer being selectable. */

window.JCARD = window.JCARD || {};

JCARD.exporter = (function () {
  const NS = 'http://www.w3.org/2000/svg';

  function jsPDFCtor() {
    const ns = window.jspdf || window.jsPDF;
    return (ns && ns.jsPDF) || window.jsPDF;
  }

  function svg2pdfFn() {
    return (window.svg2pdf && window.svg2pdf.svg2pdf) || window.svg2pdf;
  }

  /* Replace every <text> with the equivalent <path>. The rotation transform is
     carried across untouched, so a rotated run outlines exactly where it was
     painted. */
  function outlineText(svg) {
    Array.from(svg.querySelectorAll('text')).forEach((node) => {
      const font = node.getAttribute('data-font');
      const weight = Number(node.getAttribute('data-weight'));
      const size = Number(node.getAttribute('data-size'));
      const tracking = Number(node.getAttribute('data-tracking')) || 0;
      const anchor = node.getAttribute('data-anchor') || 'start';
      const text = node.textContent;

      if (!font || !text) {
        node.remove();
        return;
      }

      const d = JCARD.fonts.outline(
        font, weight, text,
        Number(node.getAttribute('x')), Number(node.getAttribute('y')),
        size, tracking, anchor
      );

      if (!d) {
        node.remove();
        return;
      }

      const path = document.createElementNS(NS, 'path');
      path.setAttribute('d', d);
      path.setAttribute('fill', node.getAttribute('fill') || '#000000');
      const transform = node.getAttribute('transform');
      if (transform) path.setAttribute('transform', transform);
      node.replaceWith(path);
    });
  }

  function prepare(svg, opts) {
    Array.from(svg.querySelectorAll('.jc-placeholder')).forEach((n) => n.remove());
    if (!opts.foldGuides) {
      Array.from(svg.querySelectorAll('.jc-guide')).forEach((n) => n.remove());
    }
    outlineText(svg);
    return svg;
  }

  /* Short-edge duplex turns the sheet about its horizontal axis, so the back
     needs a further 180-degree turn on top of the reversed panel order. */
  function rotate180(svg) {
    const g = JCARD.geom;
    const group = document.createElementNS(NS, 'g');
    group.setAttribute('transform', `rotate(180 ${g.CARD_W / 2} ${g.CARD_H / 2})`);
    while (svg.firstChild) group.appendChild(svg.firstChild);
    svg.appendChild(group);
    return svg;
  }

  function marks(doc, origin) {
    const g = JCARD.geom;
    const { len, gap, weight } = g.cropMark;
    const { x, y } = origin;
    const r = x + g.CARD_W;
    const b = y + g.CARD_H;

    doc.setDrawColor(0);
    doc.setLineWidth(weight);

    [[x, y], [r, y], [x, b], [r, b]].forEach(([cx, cy]) => {
      const hx = cx === x ? -1 : 1;
      const vy = cy === y ? -1 : 1;
      doc.line(cx + hx * gap, cy, cx + hx * (gap + len), cy);
      doc.line(cx, cy + vy * gap, cx, cy + vy * (gap + len));
    });

    // Fold ticks, keyed to whichever face this page is.
    (origin.folds || []).forEach((fx) => {
      doc.line(x + fx, y - gap, x + fx, y - gap - len);
      doc.line(x + fx, b + gap, x + fx, b + gap + len);
    });
  }

  async function renderPage(doc, svg, origin) {
    const g = JCARD.geom;
    const host = document.createElement('div');
    host.style.cssText = 'position:absolute;left:-10000px;top:0;';
    host.appendChild(svg);
    document.body.appendChild(host);
    try {
      await svg2pdfFn()(svg, doc, {
        x: origin.x, y: origin.y, width: g.CARD_W, height: g.CARD_H,
      });
    } finally {
      host.remove();
    }
    marks(doc, origin);
  }

  async function build(state) {
    const g = JCARD.geom;
    // Outlining needs the real bytes; a lazily-loaded family may not be in yet.
    await JCARD.fonts.ensureFor(state);
    const JsPDF = jsPDFCtor();
    const doc = new JsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });

    const x = (g.sheet.w - g.CARD_W) / 2;
    const y = (g.sheet.h - g.CARD_H) / 2;

    const opts = { foldGuides: state.toggles.foldGuides };

    const outside = prepare(JCARD.render.buildOutside(state, { guides: false, idPrefix: 'pdf1' }), opts);
    await renderPage(doc, outside, { x, y, folds: state.toggles.cropMarks ? g.foldsOutside : [] });

    doc.addPage();
    let inside = prepare(JCARD.render.buildInside(state, { guides: false, idPrefix: 'pdf2' }), opts);
    if (state.duplex === 'short') inside = rotate180(inside);
    await renderPage(doc, inside, { x, y, folds: state.toggles.cropMarks ? g.foldsInside : [] });

    return doc;
  }

  async function download(state, filename) {
    const doc = await build(state);
    doc.save(filename || 'ultraferric-jcard.pdf');
  }

  /* A two-page sheet with nothing but the trim box and a corner index, for
     checking that a given printer's duplex flip lines the sides up before
     anyone commits artwork and card stock to it. */
  async function alignmentTest(state) {
    const g = JCARD.geom;
    const JsPDF = jsPDFCtor();
    const doc = new JsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
    const x = (g.sheet.w - g.CARD_W) / 2;
    const y = (g.sheet.h - g.CARD_H) / 2;

    [['FRONT', g.foldsOutside], ['BACK', g.foldsInside]].forEach(([label, folds], page) => {
      if (page) doc.addPage();
      doc.setDrawColor(0);
      doc.setLineWidth(0.3);
      doc.rect(x, y, g.CARD_W, g.CARD_H);
      doc.setLineWidth(0.2);
      folds.forEach((fx) => doc.line(x + fx, y, x + fx, y + g.CARD_H));

      doc.setFontSize(10);
      doc.text(label, x + 3, y + 6);
      doc.text(`duplex: ${state.duplex} edge`, x + 3, y + 11);

      // A filled corner square: after a correct flip the two squares sit back
      // to back. If they end up diagonally opposite, switch the flip setting.
      doc.setFillColor(0);
      const sq = 8;
      if (page === 0) doc.rect(x + g.CARD_W - sq - 3, y + 3, sq, sq, 'F');
      else if (state.duplex === 'short') doc.rect(x + 3, y + g.CARD_H - sq - 3, sq, sq, 'F');
      else doc.rect(x + 3, y + 3, sq, sq, 'F');
    });

    doc.save('ultraferric-jcard-alignment-test.pdf');
  }

  return { build, download, alignmentTest, outlineText };
})();
